import React from 'react';
import ReactMarkdown from 'react-markdown';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';

const QuestionPanel = ({
  currentQuestionIndex,
  questions,
  userAnswers,
  handleAnswerChange,
  back,
  next,
  submitAnswer,
  evaluation,
  isEvaluating,
  isLastQuestion,
  onFinish,
  topic,
}) => {
  const currentQuestion = questions.find(question => question.id === currentQuestionIndex);
  const hasEvaluation = evaluation !== null && evaluation !== undefined;
  const currentAnswer = userAnswers[currentQuestionIndex] || '';

  return (
    <ExerciseCard>
      {/* Header */}
      <div className="bg-blue-500 text-white text-center py-3 px-4 rounded-lg mb-4">
        <h5 className="font-bold text-lg">
          Otazka {currentQuestionIndex} z {questions.length}
        </h5>
      </div>

      {/* Question */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
        <h3 className="text-center text-lg font-semibold text-gray-800">
          <TranslatableText text={currentQuestion.question} textColor="#1f2937" topic={topic} />
        </h3>
      </div>

      {/* Answer input */}
      <div className="mb-4">
        <label className="block text-lg font-bold text-gray-700 mb-2">
          Odpovězte celou větou:
        </label>
        <input
          type="text"
          className={`w-full p-4 text-lg rounded-lg border-2 transition-all ${
            hasEvaluation
              ? evaluation.is_correct
                ? 'border-green-500 bg-green-50'
                : 'border-red-500 bg-red-50'
              : 'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
          }`}
          value={currentAnswer}
          onChange={handleAnswerChange}
          placeholder="Napiste odpověď zde..."
          disabled={hasEvaluation || isEvaluating}
        />
      </div>

      {/* Feedback */}
      {hasEvaluation && (
        <div className={`p-4 rounded-lg mb-4 ${
          evaluation.is_correct ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {evaluation.is_correct ? (
              <span className="bg-green-500 text-white px-3 py-1 rounded-lg text-sm font-semibold">✓ Správně</span>
            ) : (
              <span className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm font-semibold">✗ Špatně</span>
            )}
          </div>
          <div className="mt-2 text-gray-700">
            <ReactMarkdown>{evaluation.evaluation}</ReactMarkdown>
          </div>
          {!evaluation.is_correct && (
            <div className="mt-3 p-3 bg-white rounded-lg">
              <strong>Správná odpověď:</strong> {currentQuestion.answer}
              {currentQuestion.alternative_answers && currentQuestion.alternative_answers.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  <strong>Další možné odpovědi:</strong>{' '}
                  {currentQuestion.alternative_answers.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading indicator */}
      {isEvaluating && (
        <div className="bg-blue-100 border border-blue-300 text-blue-700 p-4 rounded-lg mb-4 text-center">
          <div className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
          Vyhodnocuji vaši odpověď...
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={back}
          disabled={currentQuestionIndex === 1 || isEvaluating}
          className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all ${
            currentQuestionIndex === 1 || isEvaluating
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gray-500 hover:bg-gray-600 text-white'
          }`}
        >
          ← Zpět
        </button>

        {!hasEvaluation ? (
          <button
            onClick={submitAnswer}
            disabled={isEvaluating || !currentAnswer.trim()}
            className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all ${
              isEvaluating || !currentAnswer.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {isEvaluating ? 'Vyhodnocuji...' : 'Odeslat odpověď'}
          </button>
        ) : isLastQuestion ? (
          <button
            onClick={onFinish}
            className="flex-1 py-3 px-4 rounded-lg font-bold bg-green-500 hover:bg-green-600 text-white transition-all"
          >
            Zobrazit výsledky
          </button>
        ) : (
          <button
            onClick={next}
            className="flex-1 py-3 px-4 rounded-lg font-bold bg-blue-500 hover:bg-blue-600 text-white transition-all"
          >
            Další otázka →
          </button>
        )}
      </div>
    </ExerciseCard>
  );
};

export default QuestionPanel;
